import { ApiService } from '../../services/api';
import { PlanogramComplianceConfigItem } from '../../types';
import {
  BulkIngestResponse,
  DatasetsListResponse,
  EvalGtDataset,
  EvalGtSample,
  EvalModel,
  EvalRun,
  GtSampleRunsResponse,
  GtSamplesListResponse,
  MetricsOverviewResponse,
  ModelsListResponse,
  PrInferencesListResponse,
  RunInferenceDetail,
  RunInferencesListResponse,
  RunsListResponse,
} from '../types';

const API_DL_URL = process.env.REACT_APP_API_DL_URL;

if (!API_DL_URL) {
  throw new Error('REACT_APP_API_DL_URL is required for Eval API requests');
}

export class EvalApiError extends Error {
  status: number;

  detail?: string;

  constructor(message: string, status: number, detail?: string) {
    super(message);
    this.name = 'EvalApiError';
    this.status = status;
    this.detail = detail;
  }
}

async function parseApiError(response: Response, fallback: string): Promise<EvalApiError> {
  const payload = await response.json().catch(() => null);
  return new EvalApiError(payload?.detail || fallback, response.status, payload?.detail);
}

function authHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}` };
}

function jsonHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export class EvalApiService {
  static async syncModels(token: string): Promise<ModelsListResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/models/sync`, {
      method: 'POST',
      headers: authHeaders(token),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error syncing models');
    return response.json();
  }

  static async updateEmbeddingCounts(token: string): Promise<ModelsListResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/models/update-embedding-counts`, {
      method: 'POST',
      headers: authHeaders(token),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error updating embedding counts');
    return response.json();
  }

  static async listModels(
    token: string,
    params?: { model_type?: string; deployment_status?: string }
  ): Promise<ModelsListResponse> {
    const qs = new URLSearchParams();
    if (params?.model_type) qs.set('model_type', params.model_type);
    if (params?.deployment_status) qs.set('deployment_status', params.deployment_status);
    const query = qs.toString() ? `?${qs}` : '';
    const response = await fetch(`${API_DL_URL}/v1/eval/models${query}`, {
      headers: authHeaders(token),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error listing models');
    return response.json();
  }

  static async getModel(token: string, modelId: string): Promise<EvalModel | null> {
    const response = await fetch(`${API_DL_URL}/v1/eval/models/${encodeURIComponent(modelId)}`, {
      headers: authHeaders(token),
    });
    ApiService.handleError(response);
    if (response.status === 404) return null;
    if (!response.ok) throw await parseApiError(response, 'Error fetching model');
    return response.json();
  }

  static async patchModel(
    token: string,
    modelId: string,
    body: {
      description?: string | null;
      version?: string | null;
      deployment_status?: string | null;
      config?: Record<string, unknown> | null;
      metadata?: Record<string, unknown> | null;
    }
  ): Promise<EvalModel> {
    const response = await fetch(`${API_DL_URL}/v1/eval/models/${encodeURIComponent(modelId)}`, {
      method: 'PATCH',
      headers: jsonHeaders(token),
      body: JSON.stringify(body),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error updating model');
    return response.json();
  }

  static async fetchPcConfigDescriptors(token: string): Promise<PlanogramComplianceConfigItem[]> {
    const response = await fetch(`${API_DL_URL}/v1/eval/pc-config-descriptors`, {
      headers: authHeaders(token),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error fetching PC config descriptors');
    return response.json();
  }

  static async createPcModel(
    token: string,
    body: {
      service_model_id: string;
      version: string;
      description?: string;
      deployment_status: string;
      config?: Record<string, unknown> | null;
    }
  ): Promise<EvalModel> {
    const response = await fetch(`${API_DL_URL}/v1/eval/models/pc`, {
      method: 'POST',
      headers: jsonHeaders(token),
      body: JSON.stringify(body),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error creating PC model');
    return response.json();
  }

  static async listDatasetTags(token: string): Promise<string[]> {
    const response = await fetch(`${API_DL_URL}/v1/eval/datasets/tags`, {
      headers: authHeaders(token),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error listing dataset tags');
    return response.json();
  }

  static async listDatasets(
    token: string,
    params?: { status?: string }
  ): Promise<DatasetsListResponse> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    const query = qs.toString() ? `?${qs}` : '';
    const response = await fetch(`${API_DL_URL}/v1/eval/datasets${query}`, {
      headers: authHeaders(token),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error listing datasets');
    return response.json();
  }

  static async getDataset(token: string, datasetId: string): Promise<EvalGtDataset | null> {
    const response = await fetch(
      `${API_DL_URL}/v1/eval/datasets/${encodeURIComponent(datasetId)}`,
      { headers: authHeaders(token) }
    );
    ApiService.handleError(response);
    if (response.status === 404) return null;
    if (!response.ok) throw await parseApiError(response, 'Error fetching dataset');
    return response.json();
  }

  static async patchDataset(
    token: string,
    datasetId: string,
    body: { dataset_name?: string; description?: string; tags?: string[] }
  ): Promise<EvalGtDataset> {
    const response = await fetch(
      `${API_DL_URL}/v1/eval/datasets/${encodeURIComponent(datasetId)}`,
      {
        method: 'PATCH',
        headers: jsonHeaders(token),
        body: JSON.stringify(body),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error updating dataset');
    return response.json();
  }

  static async createDataset(
    token: string,
    body: { dataset_name: string; description?: string; tags?: string[] }
  ): Promise<EvalGtDataset> {
    const response = await fetch(`${API_DL_URL}/v1/eval/datasets`, {
      method: 'POST',
      headers: jsonHeaders(token),
      body: JSON.stringify(body),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error creating dataset');
    return response.json();
  }

  static async lockDataset(token: string, datasetId: string): Promise<EvalGtDataset> {
    const response = await fetch(
      `${API_DL_URL}/v1/eval/datasets/${encodeURIComponent(datasetId)}/lock`,
      { method: 'POST', headers: authHeaders(token) }
    );
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error locking dataset');
    return response.json();
  }

  static async addInferencesToDataset(
    token: string,
    datasetId: string,
    body: { gt_sample_ids: string[] }
  ): Promise<{ sample_count: number }> {
    const response = await fetch(
      `${API_DL_URL}/v1/eval/datasets/${encodeURIComponent(datasetId)}/samples`,
      {
        method: 'POST',
        headers: jsonHeaders(token),
        body: JSON.stringify(body),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error adding samples to dataset');
    return response.json();
  }

  static async removeInferenceFromDataset(
    token: string,
    datasetId: string,
    gtSampleId: string
  ): Promise<void> {
    const response = await fetch(
      `${API_DL_URL}/v1/eval/datasets/${encodeURIComponent(datasetId)}/samples/${encodeURIComponent(gtSampleId)}`,
      { method: 'DELETE', headers: authHeaders(token) }
    );
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error removing sample from dataset');
  }

  static async listGtSamples(
    token: string,
    params?: {
      dataset_id?: string;
      unassigned?: boolean;
      label_source?: string;
      store_id?: number;
      chain_id?: number;
      ingested_by?: string;
      image_id?: number;
    }
  ): Promise<GtSamplesListResponse> {
    const qs = new URLSearchParams();
    if (params?.dataset_id) qs.set('dataset_id', params.dataset_id);
    if (params?.unassigned) qs.set('unassigned', 'true');
    if (params?.label_source) qs.set('label_source', params.label_source);
    if (params?.store_id != null) qs.set('store_id', String(params.store_id));
    if (params?.chain_id != null) qs.set('chain_id', String(params.chain_id));
    if (params?.ingested_by) qs.set('ingested_by', params.ingested_by);
    if (params?.image_id != null) qs.set('image_id', String(params.image_id));
    const query = qs.toString() ? `?${qs}` : '';
    const response = await fetch(`${API_DL_URL}/v1/eval/gt-samples${query}`, {
      headers: authHeaders(token),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error listing GT samples');
    return response.json();
  }

  static async getGtSample(token: string, gtSampleId: string): Promise<EvalGtSample | null> {
    const response = await fetch(
      `${API_DL_URL}/v1/eval/gt-samples/${encodeURIComponent(gtSampleId)}`,
      { headers: authHeaders(token) }
    );
    ApiService.handleError(response);
    if (response.status === 404) return null;
    if (!response.ok) throw await parseApiError(response, 'Error fetching GT sample');
    return response.json();
  }

  static async createGtSample(
    token: string,
    body: {
      image_id?: number;
      store_id?: number;
      camera_id?: number;
      captured_at?: string;
      label_source: string;
      source_inference_id?: string;
      pr_inference?: Record<string, unknown>;
      pc_inference?: Record<string, unknown>;
    }
  ): Promise<EvalGtSample> {
    const response = await fetch(`${API_DL_URL}/v1/eval/gt-samples`, {
      method: 'POST',
      headers: jsonHeaders(token),
      body: JSON.stringify(body),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error creating GT sample');
    return response.json();
  }

  static async correctGtSample(
    token: string,
    id: string,
    body: { field: string; value: unknown; note?: string }
  ): Promise<void> {
    const response = await fetch(`${API_DL_URL}/v1/eval/gt-samples/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: jsonHeaders(token),
      body: JSON.stringify(body),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error correcting GT sample');
  }

  static async getGtSampleRuns(token: string, gtSampleId: string): Promise<GtSampleRunsResponse> {
    const response = await fetch(
      `${API_DL_URL}/v1/eval/gt-samples/${encodeURIComponent(gtSampleId)}/runs`,
      { headers: authHeaders(token) }
    );
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error fetching GT sample runs');
    return response.json();
  }

  static async listRuns(
    token: string,
    params?: {
      status?: string;
      run_type?: string;
      dataset_id?: string;
      model_id?: string;
      limit?: number;
    }
  ): Promise<RunsListResponse> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.run_type) qs.set('run_type', params.run_type);
    if (params?.dataset_id) qs.set('dataset_id', params.dataset_id);
    if (params?.model_id) qs.set('model_id', params.model_id);
    if (params?.limit != null) qs.set('limit', String(params.limit));
    const query = qs.toString() ? `?${qs}` : '';
    const response = await fetch(`${API_DL_URL}/v1/eval/runs${query}`, {
      headers: authHeaders(token),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error listing runs');
    return response.json();
  }

  static async getRun(token: string, runId: string): Promise<EvalRun | null> {
    const response = await fetch(`${API_DL_URL}/v1/eval/runs/${encodeURIComponent(runId)}`, {
      headers: authHeaders(token),
    });
    ApiService.handleError(response);
    if (response.status === 404) return null;
    if (!response.ok) throw await parseApiError(response, 'Error fetching run');
    return response.json();
  }

  static async scheduleRun(
    token: string,
    body: {
      run_type: string;
      model_ids: { OD?: string | null; PR?: string | null; PC?: string | null };
      dataset_id: string;
      source_run_id?: string | null;
      config?: Record<string, unknown>;
    }
  ): Promise<EvalRun> {
    const response = await fetch(`${API_DL_URL}/v1/eval/runs`, {
      method: 'POST',
      headers: jsonHeaders(token),
      body: JSON.stringify(body),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error scheduling run');
    return response.json();
  }

  static async cancelRun(token: string, runId: string): Promise<EvalRun> {
    const response = await fetch(`${API_DL_URL}/v1/eval/runs/${encodeURIComponent(runId)}/cancel`, {
      method: 'POST',
      headers: jsonHeaders(token),
      body: '{}',
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error cancelling run');
    return response.json();
  }

  static async retryRun(token: string, runId: string): Promise<EvalRun> {
    const response = await fetch(`${API_DL_URL}/v1/eval/runs/${encodeURIComponent(runId)}/retry`, {
      method: 'POST',
      headers: authHeaders(token),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error retrying run');
    return response.json();
  }

  static async deleteRun(token: string, runId: string): Promise<void> {
    const response = await fetch(`${API_DL_URL}/v1/eval/runs/${encodeURIComponent(runId)}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error deleting run');
  }

  static async patchRunComment(
    token: string,
    runId: string,
    comment: string | null
  ): Promise<EvalRun> {
    const response = await fetch(
      `${API_DL_URL}/v1/eval/runs/${encodeURIComponent(runId)}/comment`,
      {
        method: 'PATCH',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment }),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error updating run comment');
    return response.json();
  }

  static async timeoutRun(token: string, runId: string): Promise<EvalRun> {
    const response = await fetch(
      `${API_DL_URL}/v1/eval/runs/${encodeURIComponent(runId)}/timeout`,
      { method: 'POST', headers: authHeaders(token) }
    );
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error timing out run');
    return response.json();
  }

  static async getRunInferences(
    token: string,
    runId: string,
    params?: { offset?: number; limit?: number }
  ): Promise<RunInferencesListResponse> {
    const qs = new URLSearchParams();
    if (params?.offset != null) qs.set('offset', String(params.offset));
    if (params?.limit != null) qs.set('limit', String(params.limit));
    const query = qs.toString() ? `?${qs}` : '';
    const response = await fetch(
      `${API_DL_URL}/v1/eval/runs/${encodeURIComponent(runId)}/inferences${query}`,
      { headers: authHeaders(token) }
    );
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error fetching run inferences');
    return response.json();
  }

  static async getRunInference(
    token: string,
    runId: string,
    runInferenceId: string
  ): Promise<RunInferenceDetail | null> {
    const response = await fetch(
      `${API_DL_URL}/v1/eval/runs/${encodeURIComponent(runId)}/inferences/${encodeURIComponent(runInferenceId)}`,
      { headers: authHeaders(token) }
    );
    ApiService.handleError(response);
    if (response.status === 404) return null;
    if (!response.ok) throw await parseApiError(response, 'Error fetching run inference detail');
    return response.json();
  }

  static async listPrInferences(
    token: string,
    params?: {
      skip?: number;
      limit?: number;
      store_id?: number;
      chain_id?: number;
      camera_id?: number;
    }
  ): Promise<PrInferencesListResponse> {
    const qs = new URLSearchParams();
    if (params?.skip != null) qs.set('skip', String(params.skip));
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.store_id != null) qs.set('store_id', String(params.store_id));
    if (params?.chain_id != null) qs.set('chain_id', String(params.chain_id));
    if (params?.camera_id != null) qs.set('camera_id', String(params.camera_id));
    const query = qs.toString() ? `?${qs}` : '';
    const response = await fetch(`${API_DL_URL}/v1/eval/pr-inferences${query}`, {
      headers: authHeaders(token),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error listing PR inferences');
    return response.json();
  }

  static async bulkIngestFromPrInferences(
    token: string,
    inferenceIds: string[]
  ): Promise<BulkIngestResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/gt-samples/from-inferences`, {
      method: 'POST',
      headers: jsonHeaders(token),
      body: JSON.stringify({ inference_ids: inferenceIds }),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error ingesting inferences');
    return response.json();
  }

  static async getMetrics(
    token: string,
    datasetId: string,
    runType?: string
  ): Promise<MetricsOverviewResponse> {
    const qs = new URLSearchParams({ dataset_id: datasetId });
    if (runType) qs.set('run_type', runType);
    const response = await fetch(`${API_DL_URL}/v1/eval/metrics?${qs}`, {
      headers: authHeaders(token),
    });
    ApiService.handleError(response);
    if (!response.ok) throw await parseApiError(response, 'Error fetching metrics');
    return response.json();
  }
}
